/**
 * Partner Outreach Campaign Templates
 * Based on best practices from HubSpot, Salesforce, Shopify, and AWS partner programs
 *
 * Key insights from world-class partner programs:
 * - Partner-sourced deals close 46% faster (Salesforce)
 * - Partners expand your reach without expanding headcount
 * - Focus on partner success, not just your revenue
 * - Provide enablement resources and co-marketing support
 * - Create clear economic incentives (margins, SPIFs, MDF)
 */

import { CampaignTemplate } from "./types";

export const PARTNER_AGENCY_RECRUITMENT: CampaignTemplate = {
  id: "partner-agency-recruitment",
  name: "Agency Partner Recruitment",
  category: "partner",
  description: "Recruit digital agencies to your partner program.",
  strategy:
    "Based on HubSpot's agency partner program approach. Focus on how partnership creates new revenue streams for the agency, makes them more competitive, and provides resources they can't build themselves.",
  icon: "TeamOutlined",
  color: "#6366f1",
  targetAudience: "agency",
  personalizationLevel: "high",

  expectedOutcomes: [
    "New agency partners",
    "Multiple client implementations per partner",
    "Recurring partner-sourced revenue",
    "Expanded market reach",
  ],

  bestPractices: [
    "Research the agency's client base and services",
    "Focus on revenue opportunity for the agency",
    "Highlight enablement and support resources",
    "Mention exclusive benefits (early access, co-marketing)",
    "Share success stories from similar agencies",
  ],

  objectionHandling: [
    "'We already have partnerships' → We complement, not compete - show integration",
    "'Not enough demand from clients' → We help you create demand with co-marketing",
    "'Don't have time to learn new tools' → Our certification takes 2 hours",
  ],

  segmentation: {
    leadSource: ["linkedin", "website", "referral", "event"],
    companySize: ["1-10", "11-50", "51-200"],
    industries: [
      "Marketing Agency",
      "Digital Agency",
      "Web Development",
      "Consulting",
    ],
    jobTitles: [
      "Agency Owner",
      "CEO",
      "Founder",
      "Managing Partner",
      "Partner",
      "Managing Director",
    ],
    requiredFields: ["first_name", "company_name", "email"],
  },

  emails: [
    {
      name: "Day 0 - Partnership Opportunity",
      subject: "{{first_name}}, new revenue stream for {{company_name}}",
      suggestedMjmlTemplateId: "coldOutreach-partnerOpportunity",
      delayAfterPrevious: { value: 0, unit: "days" },
      purpose: "Introduce partnership opportunity with revenue focus",
      optimalSendTime: {
        dayOfWeek: ["tuesday", "wednesday", "thursday"],
        timeSlot: "morning",
      },
      variables: [
        {
          key: "revenue_opportunity",
          defaultValue: "add $5-10K MRR with our AI chatbot solution",
          description: "Revenue opportunity for partner",
        },
        {
          key: "partner_benefit",
          defaultValue: "offer clients something competitors can't",
          description: "Key benefit for the agency",
        },
        {
          key: "margin",
          defaultValue: "30-50%",
          description: "Partner margin/markup",
        },
      ],
    },
    {
      name: "Day 4 - Partner Benefits",
      subject: "What {{company_name}} gets as a partner",
      suggestedMjmlTemplateId: "coldOutreach-partnerBenefits",
      delayAfterPrevious: { value: 4, unit: "days" },
      purpose: "Detail the partner program benefits",
      variables: [
        {
          key: "benefit_1",
          defaultValue: "30-50% margin on all client implementations",
          description: "First benefit",
        },
        {
          key: "benefit_2",
          defaultValue: "Co-marketing funds and campaigns",
          description: "Second benefit",
        },
        {
          key: "benefit_3",
          defaultValue: "Priority support and dedicated partner manager",
          description: "Third benefit",
        },
        {
          key: "benefit_4",
          defaultValue: "Early access to new features",
          description: "Fourth benefit",
        },
      ],
    },
    {
      name: "Day 9 - Agency Success Story",
      subject: "How {{partner_agency}} added $15K MRR",
      suggestedMjmlTemplateId: "coldOutreach-partnerSuccessStory",
      delayAfterPrevious: { value: 5, unit: "days" },
      purpose: "Share success story from similar agency partner",
      variables: [
        {
          key: "partner_agency",
          defaultValue: "a digital marketing agency like yours",
          description: "Reference partner agency",
        },
        {
          key: "partner_result",
          defaultValue: "$15K in new monthly recurring revenue",
          description: "Results achieved by partner",
        },
        {
          key: "partner_clients",
          defaultValue: "12 client implementations",
          description: "Number of clients served",
        },
      ],
    },
    {
      name: "Day 14 - Partner Call",
      subject: "Partner discussion, {{first_name}}?",
      suggestedMjmlTemplateId: "coldOutreach-partnerCall",
      delayAfterPrevious: { value: 5, unit: "days" },
      purpose: "Request partner program discussion call",
    },
  ],

  sendingConfig: {
    emailsPerDayPerSender: 40,
    dailyLimit: 120,
    requiresWarming: true,
  },

  variables: [
    {
      key: "sender_name",
      defaultValue: "{{user_name}}",
      description: "Partner manager name",
      source: "crm",
    },
    {
      key: "sender_title",
      defaultValue: "Partner Manager",
      description: "Sender's title",
      source: "manual",
    },
    {
      key: "partner_portal_url",
      defaultValue: "{{asset_url}}",
      description: "Partner program page",
      source: "manual",
    },
    {
      key: "calendar_link",
      defaultValue: "{{booking_link}}",
      description: "Meeting booking link",
      source: "crm",
    },
  ],

  tags: ["partner", "agency", "recruitment", "channel", "reseller"],
  bestFor: ["Partner Programs", "Channel Sales", "Agency Partnerships"],
  difficulty: "intermediate",
  setupTime: "20 minutes",
  inspiredBy: ["HubSpot Partner Program", "Shopify Partners", "Klaviyo Partners"],
};

export const PARTNER_RESELLER_PROGRAM: CampaignTemplate = {
  id: "partner-reseller-program",
  name: "Reseller Program Invitation",
  category: "partner",
  description: "Recruit resellers to distribute your product.",
  strategy:
    "Based on software reseller program best practices. Focus on the economic opportunity, sales support provided, and exclusivity/territory options. Resellers want clear margins, demand generation support, and a proven product.",
  icon: "ShopOutlined",
  color: "#10b981",
  targetAudience: "generic",
  personalizationLevel: "moderate",

  expectedOutcomes: [
    "New reseller partnerships",
    "Geographic/vertical expansion",
    "Reduced direct sales costs",
    "Faster market penetration",
  ],

  bestPractices: [
    "Clearly define margins and deal registration",
    "Offer territory or vertical exclusivity options",
    "Provide sales tools and training",
    "Create demand with marketing support",
    "Make deal registration and quoting easy",
  ],

  objectionHandling: [
    "'Margins too low' → Show volume potential and deal registration benefits",
    "'Already reselling competitors' → Our product complements or beats their offering",
    "'No demand in my market' → We provide demand generation support",
  ],

  segmentation: {
    leadSource: ["linkedin", "website", "event", "referral"],
    companySize: ["11-50", "51-200"],
    industries: [
      "IT Services",
      "Software Reseller",
      "MSP",
      "Consulting",
      "VAR",
    ],
    jobTitles: [
      "CEO",
      "Owner",
      "VP of Sales",
      "Business Development",
      "Managing Director",
    ],
    requiredFields: ["first_name", "company_name", "email"],
  },

  emails: [
    {
      name: "Day 0 - Reseller Opportunity",
      subject: "{{first_name}}, reseller opportunity in {{market}}",
      suggestedMjmlTemplateId: "coldOutreach-resellerOpportunity",
      delayAfterPrevious: { value: 0, unit: "days" },
      purpose: "Introduce reseller program with market opportunity",
      optimalSendTime: {
        dayOfWeek: ["tuesday", "wednesday", "thursday"],
        timeSlot: "morning",
      },
      variables: [
        {
          key: "market",
          defaultValue: "your market",
          description: "Their market or territory",
        },
        {
          key: "market_opportunity",
          defaultValue: "AI chatbot market growing 25% annually",
          description: "Market opportunity",
        },
        {
          key: "reseller_margin",
          defaultValue: "35-50% margins",
          description: "Reseller margins",
        },
      ],
    },
    {
      name: "Day 4 - Program Details",
      subject: "Reseller program details for {{company_name}}",
      suggestedMjmlTemplateId: "coldOutreach-resellerDetails",
      delayAfterPrevious: { value: 4, unit: "days" },
      purpose: "Share program structure and benefits",
      variables: [
        {
          key: "tier_1",
          defaultValue: "Authorized Reseller: 25% margin",
          description: "First tier benefits",
        },
        {
          key: "tier_2",
          defaultValue: "Premium Reseller: 35% margin + MDF",
          description: "Second tier benefits",
        },
        {
          key: "tier_3",
          defaultValue: "Elite Reseller: 50% margin + exclusivity",
          description: "Third tier benefits",
        },
      ],
    },
    {
      name: "Day 9 - Sales Support",
      subject: "How we help you sell",
      suggestedMjmlTemplateId: "coldOutreach-salesSupport",
      delayAfterPrevious: { value: 5, unit: "days" },
      purpose: "Detail the sales support and enablement provided",
      variables: [
        {
          key: "support_1",
          defaultValue: "Sales training and certification",
          description: "First support item",
        },
        {
          key: "support_2",
          defaultValue: "Lead sharing and deal registration",
          description: "Second support item",
        },
        {
          key: "support_3",
          defaultValue: "Co-branded materials and demos",
          description: "Third support item",
        },
      ],
    },
    {
      name: "Day 14 - Discussion",
      subject: "Discuss the reseller program, {{first_name}}?",
      suggestedMjmlTemplateId: "coldOutreach-resellerDiscussion",
      delayAfterPrevious: { value: 5, unit: "days" },
      purpose: "Request call to discuss program fit",
    },
  ],

  sendingConfig: {
    emailsPerDayPerSender: 45,
    dailyLimit: 135,
    requiresWarming: true,
  },

  variables: [
    {
      key: "sender_name",
      defaultValue: "{{user_name}}",
      description: "Channel manager name",
      source: "crm",
    },
    {
      key: "sender_title",
      defaultValue: "Channel Manager",
      description: "Sender's title",
      source: "manual",
    },
    {
      key: "market",
      defaultValue: "your region",
      description: "Their market/territory",
      source: "enrichment",
    },
    {
      key: "calendar_link",
      defaultValue: "{{booking_link}}",
      description: "Meeting booking link",
      source: "crm",
    },
  ],

  tags: ["partner", "reseller", "channel", "var", "distribution"],
  bestFor: ["Channel Programs", "Geographic Expansion", "VAR Partnerships"],
  difficulty: "intermediate",
  setupTime: "20 minutes",
  inspiredBy: ["AWS Partner Program", "Microsoft Partner Network", "Cisco Partners"],
};

export const PARTNER_INTEGRATION: CampaignTemplate = {
  id: "partner-integration",
  name: "Integration Partnership",
  category: "partner",
  description: "Propose integration or technology partnerships.",
  strategy:
    "Based on platform ecosystem development best practices. Focus on mutual customer value - how the integration helps both companies' customers achieve better outcomes. Start with technical fit, then move to go-to-market.",
  icon: "ApiOutlined",
  color: "#8b5cf6",
  targetAudience: "b2b-saas",
  personalizationLevel: "high",

  expectedOutcomes: [
    "New integration partnerships",
    "Access to partner's customer base",
    "Co-marketing opportunities",
    "Product ecosystem expansion",
  ],

  bestPractices: [
    "Lead with customer value, not partnership asks",
    "Show you've done homework on their product",
    "Propose specific integration use cases",
    "Start technical, then involve business development",
    "Offer to build the integration first",
  ],

  objectionHandling: [
    "'We have many integration requests' → We'll build it, you just need to review",
    "'Our customers aren't asking for this' → Here's demand data from our customers",
    "'We prefer native features' → Integration extends your platform's value",
  ],

  segmentation: {
    leadSource: ["linkedin", "website", "referral"],
    companySize: ["11-50", "51-200", "201-500", "501-1000"],
    industries: ["SaaS", "Technology", "Platform"],
    jobTitles: [
      "Head of Partnerships",
      "VP of Business Development",
      "CTO",
      "Head of Product",
      "CEO",
      "Founder",
    ],
    requiredFields: ["first_name", "company_name", "email", "job_title"],
  },

  emails: [
    {
      name: "Day 0 - Integration Value",
      subject: "{{first_name}}, {{product_name}} + {{their_product}} integration",
      suggestedMjmlTemplateId: "coldOutreach-integrationValue",
      delayAfterPrevious: { value: 0, unit: "days" },
      purpose: "Propose integration with clear customer value",
      optimalSendTime: {
        dayOfWeek: ["tuesday", "wednesday"],
        timeSlot: "morning",
      },
      variables: [
        {
          key: "product_name",
          defaultValue: "Conferbot",
          description: "Your product name",
        },
        {
          key: "their_product",
          defaultValue: "{{company_name}}",
          description: "Their product name",
        },
        {
          key: "integration_value",
          defaultValue: "help mutual customers automate customer conversations directly within their workflow",
          description: "Value of the integration",
        },
        {
          key: "use_case",
          defaultValue: "trigger AI conversations from customer events in your platform",
          description: "Specific use case",
        },
      ],
    },
    {
      name: "Day 5 - Customer Demand",
      subject: "Demand for {{product_name}} + {{their_product}}",
      suggestedMjmlTemplateId: "coldOutreach-integrationDemand",
      delayAfterPrevious: { value: 5, unit: "days" },
      purpose: "Show customer demand for the integration",
      variables: [
        {
          key: "demand_evidence",
          defaultValue: "We've had 50+ customers ask about this integration",
          description: "Evidence of demand",
        },
        {
          key: "mutual_customers",
          defaultValue: "We share several customers who would benefit immediately",
          description: "Mutual customer opportunity",
        },
      ],
    },
    {
      name: "Day 11 - We'll Build It",
      subject: "We'll build the integration, {{first_name}}",
      suggestedMjmlTemplateId: "coldOutreach-integrationBuild",
      delayAfterPrevious: { value: 6, unit: "days" },
      purpose: "Offer to do the technical work",
      variables: [
        {
          key: "build_offer",
          defaultValue: "We'll handle the development, you just need to review",
          description: "What you're offering to do",
        },
        {
          key: "timeline",
          defaultValue: "4-6 weeks",
          description: "Estimated build timeline",
        },
      ],
    },
    {
      name: "Day 17 - Partnership Discussion",
      subject: "Partnership call, {{first_name}}?",
      suggestedMjmlTemplateId: "coldOutreach-partnershipCall",
      delayAfterPrevious: { value: 6, unit: "days" },
      purpose: "Request partnership discussion",
    },
  ],

  sendingConfig: {
    emailsPerDayPerSender: 30,
    dailyLimit: 90,
    requiresWarming: true,
    minDomainAge: 30,
  },

  variables: [
    {
      key: "sender_name",
      defaultValue: "{{user_name}}",
      description: "Partnerships lead name",
      source: "crm",
    },
    {
      key: "sender_title",
      defaultValue: "Head of Partnerships",
      description: "Sender's title",
      source: "manual",
    },
    {
      key: "product_name",
      defaultValue: "Conferbot",
      description: "Your product name",
      source: "manual",
    },
    {
      key: "calendar_link",
      defaultValue: "{{booking_link}}",
      description: "Meeting booking link",
      source: "crm",
    },
  ],

  tags: ["partner", "integration", "api", "ecosystem", "technology"],
  bestFor: ["Platform Companies", "API Products", "Ecosystem Development"],
  difficulty: "advanced",
  setupTime: "25 minutes",
  inspiredBy: ["Zapier", "Segment", "Stripe"],
};

export const PARTNER_TEMPLATES = [
  PARTNER_AGENCY_RECRUITMENT,
  PARTNER_RESELLER_PROGRAM,
  PARTNER_INTEGRATION,
];
